import RestaurantKrunshScore from "@/components/RestaurantKrunshScore";
import Layout from "./components/Layout";
import { useEffect, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { BikeIcon, InfoIcon, StarIcon } from "lucide-react";
import RestaurantCard from "@/components/RestaurantCard";
import {
  GoogleMap,
  Marker,
  OverlayView,
  useJsApiLoader,
} from "@react-google-maps/api";
import type { RestaurantDetails, RestaurantLeaderboardEntry } from "@/API";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import { useTranslation } from "react-i18next";
import Text from "@/components/Text";
import Badge from "@/components/Badge";
import LayoutLoading from "@/components/LayoutLoading.tsx";
import { getColorKrunshScore } from "@/lib/utils";

const center = { lat: 48.858374, lng: 2.336046 };

const getBg = (value: number): string => {
  if (value < 20) return "#FFF1F1";
  if (value < 80) return "#FFF3EB";
  return "#DEFBE6";
};

const getBorder = (value: number): string => {
  if (value < 40) return "#E2483D";
  if (value < 80) return "#E56910";
  return "#198038";
};

const getTitle = (value: number): string => {
  if (value < 40) return "restaurant_public_title_low_score";
  if (value < 80) return "restaurant_public_title_middle_score";
  return "restaurant_public_title_hight_score";
};

const getDescription = (value: number): string[] => {
  if (value < 40)
    return [
      "restaurant_public_description_1_low_score",
      "restaurant_public_description_2_low_score",
      "restaurant_public_description_3_low_score",
    ];
  if (value < 80)
    return [
      "restaurant_public_description_1_middle_score",
      "restaurant_public_description_2_middle_score",
      "restaurant_public_description_3_middle_score",
    ];
  return [
    "restaurant_public_description_1_high_score",
    "restaurant_public_description_2_high_score",
    "restaurant_public_description_3_high_score",
  ];
};

const getImpact = (value: number): string => {
  if (value < 40) return "restaurant_public_impact_low_score";
  if (value < 80) return "restaurant_public_impact_middle_score";
  return "restaurant_public_impact_hight_score";
};

const PublicRestaurant = () => {
  const navigate = useNavigate();
  const { sm } = useDetectSizeDevice();
  const { t } = useTranslation();
  const { restaurantId } = useParams<{ restaurantId?: string }>();
  const { isLoaded } = useJsApiLoader({
    id: "55f6b0cbd2ae313dcf7f2bce",
    googleMapsApiKey: "AIzaSyBGejFVziYqAibaBAyZE4qh5seqgy8M8io",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [restaurant, setRestaurant] = useState<null | RestaurantDetails>(null);
  const [openOverlay, setOpenOverlay] = useState(false);
  const [positionOverLay, setPositionOverlay] = useState(center);
  const [restaurantSelected, setRestaurantSelected] =
    useState<null | RestaurantLeaderboardEntry>(null);

  useEffect(() => {
    const fetchRestaurant = async (restaurantId: string) => {
      setIsLoading(true);

      await fetch(
        `${import.meta.env.VITE_SNAPSHOT_API_ENDPOINT}/restaurants/${restaurantId}`,
      ).then(async (result) => {
        const jsonResult = await result.json();
        setRestaurant(jsonResult);
        setIsLoading(false);
      });
    };

    if (restaurantId) fetchRestaurant(restaurantId);
  }, [restaurantId]);

  if (isLoading) return <LayoutLoading />;

  if (!restaurant) return null;

  return (
    <Layout>
      <Outlet />

      <div
        style={{
          height: `calc(100dvh - var(--height-header))`,
        }}
        className={
          "flex flex-col bg-[#F7F8F9] w-[100%] p-[16px] sm:p-[24px] 2xl:px-[120px] overflow-y-auto overflow-x-hidden"
        }
      >
        <div className="flex gap-[24px] flex-col sm:flex-row">
          <div className="w-[100%] sm:w-[264px]">
            <RestaurantCard
              firstSeen={restaurant.firstSeenAt}
              deliveryTime={restaurant.deliveryTime}
              name={restaurant.name}
              address={restaurant?.address ?? ""}
              tags={
                restaurant?.categories?.filter(
                  (it) => typeof it === "string",
                ) ?? []
              }
              rate={restaurant?.rating ?? ""}
              image={restaurant?.imageUrl ?? undefined}
              platform={restaurant.platform.toLowerCase()}
              krunshScore={restaurant.krunshScore}
              linkToRestaurant={restaurant?.url ?? undefined}
            />
          </div>

          {isLoaded && (
            <GoogleMap
              center={{
                lat: restaurant.position.latitude,
                lng: restaurant.position.longitude,
              }}
              zoom={16}
              onClick={() => {
                setRestaurantSelected(null);
                setOpenOverlay(false);
              }}
              clickableIcons={false}
              mapContainerStyle={{
                width: "100%",
                borderRadius: 12,
                height: sm ? "auto" : 300,
              }}
              options={{
                fullscreenControl: false,
                mapTypeControl: false,
              }}
            >
              <Marker
                icon="/img/pins/store.svg"
                position={{
                  lat: restaurant.position.latitude,
                  lng: restaurant.position.longitude,
                }}
              />
              {restaurant.nearbyRestaurants.restaurants.map(
                (nearbyRestaurant) => {
                  if (!nearbyRestaurant) return null;

                  return (
                    <Marker
                      key={nearbyRestaurant.id}
                      icon="/img/pins/store-ranked.svg"
                      position={{
                        lat: nearbyRestaurant.position.latitude,
                        lng: nearbyRestaurant.position.longitude,
                      }}
                      onClick={() => {
                        setRestaurantSelected(nearbyRestaurant);
                        setPositionOverlay({
                          lat: nearbyRestaurant.position.latitude,
                          lng: nearbyRestaurant.position.longitude,
                        });
                        setOpenOverlay(true);
                      }}
                      clickable={true}
                    />
                  );
                },
              )}

              {openOverlay && (
                <OverlayView
                  position={positionOverLay}
                  mapPaneName={"floatPane"}
                >
                  <div className="bg-[#FFFFFF] rounded-[12px] py-[16px] px-[24px] flex flex-col w-[300px] cursor:pointer">
                    {restaurantSelected && (
                      <>
                        <div className="flex gap-[12px] items-center">
                          <img
                            className="h-[40px] w-[40px] rounded-[3px]"
                            src={restaurantSelected?.imageUrl ?? undefined}
                            alt={`image of ${restaurantSelected.name}`}
                          />
                          <div className="flex flex-col gap-[4px]">
                            <Text fontWeight={600} fontSize={14}>
                              {restaurantSelected.name}
                            </Text>
                            <Text fontWeight={400} fontSize={12}>
                              {restaurantSelected.address}
                            </Text>
                          </div>
                        </div>
                        <div className="flex items-center gap-[8px] mt-[12px]">
                          <img
                            alt={`icon of ${restaurantSelected.platform}`}
                            src={`/partner/${restaurantSelected.platform.toLowerCase()}.png`}
                            className="w-[24px] h-[24px] rounded-[54px]"
                          />
                          <div
                            className="bg-[white] ml-[-20px] h-[24px] w-[24px] rounded-[50%] flex items-center justify-center"
                            style={{
                              border: `2px solid ${getColorKrunshScore(restaurantSelected.krunshScore)}`,
                            }}
                          >
                            <Text
                              fontSize={12}
                              fontWeight={600}
                              color={getColorKrunshScore(
                                restaurantSelected.krunshScore,
                              )}
                            >
                              {restaurantSelected.krunshScore}
                            </Text>
                          </div>
                          <Badge>
                            <StarIcon height={16} width={16} color="#758195" />
                            <Text fontWeight={600} fontSize={14}>
                              {restaurantSelected.rating}
                            </Text>
                          </Badge>
                          <Badge>
                            <BikeIcon height={16} width={16} color="#758195" />
                            <Text
                              fontWeight={600}
                              fontSize={14}
                            >{`${restaurantSelected.deliveryTime} min`}</Text>
                          </Badge>
                        </div>
                      </>
                    )}
                  </div>
                </OverlayView>
              )}
            </GoogleMap>
          )}
        </div>

        <div className="flex h-[48px] min-h-[48px] items-center mt-[40px] mb-[24px] overflow-x-auto overflow-y-auto">
          <div
            style={{ borderBottom: "3px solid #1D7AFC" }}
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("krunsh_score")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon height={18} width={18} color="#44546F" />
              </TooltipTrigger>
              <TooltipContent>{t("krunsh_score_description")}</TooltipContent>
            </Tooltip>
          </div>
          <div
            onClick={() => {
              navigate("sign-up");
            }}
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("ranking")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon height={18} width={18} color="#44546F" />
              </TooltipTrigger>
              <TooltipContent>{t("ranking_description")}</TooltipContent>
            </Tooltip>
          </div>
          <div
            onClick={() => {
              navigate("sign-up");
            }}
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("menu")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon height={18} width={18} color="#44546F" />
              </TooltipTrigger>
              <TooltipContent>{t("menu_description")}</TooltipContent>
            </Tooltip>
          </div>
          <div
            onClick={() => {
              navigate("sign-up");
            }}
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("promotion")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon height={18} width={18} color="#44546F" />
              </TooltipTrigger>
              <TooltipContent>{t("promotion_description")}</TooltipContent>
            </Tooltip>
          </div>
          <div
            onClick={() => {
              navigate("sign-up");
            }}
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("compare")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon height={18} width={18} color="#44546F" />
              </TooltipTrigger>
              <TooltipContent>{t("compare_description")}</TooltipContent>
            </Tooltip>
          </div>
          <div
            onClick={() => {
              navigate("sign-up");
            }}
            className="flex items-center gap-[10px] px-[16px] h-[48px] cursor-pointer"
          >
            <Text
              fontWeight={500}
              fontSize={16}
              className="truncate leading-[24px]"
            >
              {t("general_information")}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoIcon height={18} width={18} color="#44546F" />
              </TooltipTrigger>
              <TooltipContent>
                {t("general_information_description")}
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        <div
          className="w-[100%] p-[24px] rounded-[8px] mb-[24px] flex flex-col gap-[8px]"
          style={{
            backgroundColor: getBg(restaurant.krunshScore),
            border: `solid 1px ${getBorder(restaurant.krunshScore)}`,
          }}
        >
          <Text fontSize={14} fontWeight={600} className="mb-[4px]">
            {t(getTitle(restaurant.krunshScore))}
          </Text>
          {getDescription(restaurant.krunshScore).map((text) => (
            <Text fontSize={14} fontWeight={400} key={text}>
              {t(text)}
            </Text>
          ))}
          <Text fontSize={14} fontWeight={600} className="mt-[4px]">
            {t(getImpact(restaurant.krunshScore))}
          </Text>
        </div>

        <RestaurantKrunshScore
          restaurant={restaurant ?? undefined}
          isLoading={isLoading}
          isBlured
        />
      </div>
    </Layout>
  );
};

export default PublicRestaurant;
